"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, Ban, CircleCheck as CheckCircle2, Droplets, Flame, Gauge, PhoneCall, PowerOff, ShieldAlert, Wrench, ZapOff } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
interface ProblemItem {
  id: string;
  title: string;
  badge: string;
  iconName: string;
  symptoms: string;
  causes: string;
  urgency: string;
  diySafe: boolean;
}
const problems: ProblemItem[] = [{
  id: "jams",
  title: "Jammed Flywheel & Humming Motor",
  badge: "Most Common",
  iconName: "flame",
  symptoms: "Disposal hums loudly when switched on but blades do not spin, followed by breaker tripping.",
  causes: "Hard food debris, bones, silverware, or fruit pits lodged between the shredder ring and impellers.",
  urgency: "High - motor can burn out quickly",
  diySafe: false
}, {
  id: "clogs",
  title: "Standing Water & Slow Draining Clogs",
  badge: "Urgent",
  iconName: "droplets",
  symptoms: "Water backs up into the adjacent kitchen sink bowl or drains at an agonizingly slow pace.",
  causes: "Accumulated grease, coffee grounds, fibrous vegetables (celery, potato peels), or compacted starches.",
  urgency: "Moderate to High",
  diySafe: true
}, {
  id: "leaks",
  title: "Under-Sink Water Leaks",
  badge: "Damage Risk",
  iconName: "alertTriangle",
  symptoms: "Puddles forming on cabinet floor, damp smell, or dripping under the disposal unit housing.",
  causes: "Failed mounting gasket, cracked interior housing seal, or loose dishwasher drain connection hose.",
  urgency: "Critical - prevents wood rot & mold",
  diySafe: false
}, {
  id: "power",
  title: "Unit Won't Turn On (Dead Silence)",
  badge: "Electrical",
  iconName: "powerOff",
  symptoms: "Zero sound, zero vibration, and completely unresponsive when wall switch is flipped.",
  causes: "Tripped red reset button beneath unit, tripped breaker box circuit, or burnt electrical wiring.",
  urgency: "Moderate",
  diySafe: true
}, {
  id: "dull-blades",
  title: "Dull Impellers & Persistent Odors",
  badge: "Wear & Tear",
  iconName: "wrench",
  symptoms: "Food takes much longer to grind, foul rotting smells linger, or unit makes loud rattling sounds.",
  causes: "Worn shredder teeth from age, mineral buildup, or corrosion around the grinding chamber.",
  urgency: "Low to Moderate",
  diySafe: true
}];
const safetyTips = ["Never place your bare hand or fingers inside the grinding chamber under any circumstance.", "Always turn off the dedicated electrical breaker before attempting any visual inspection.", "Avoid chemical drain cleaners - they corrode disposal internal seals and cause caustic splashes."];
export default function DisposalProblems() {
  const shouldReduceMotion = useReducedMotion();
  const getProblemIcon = (iconName: string) => {
    switch (iconName) {
      case "flame":
        return <Flame className="size-5 text-accent-foreground" />;
      case "droplets":
        return <Droplets className="size-5 text-accent-foreground" />;
      case "alertTriangle":
        return <AlertTriangle className="size-5 text-accent-foreground" />;
      case "powerOff":
        return <ZapOff className="size-5 text-accent-foreground" />;
      default:
        return <Wrench className="size-5 text-accent-foreground" />;
    }
  };
  return <section id="garbagedisposalproblems" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-semibold text-secondary-foreground">
            Fast Kitchen Diagnostics
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Common Garbage Disposal Failures
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            From loud humming jams to stubborn clogs and hazardous under-sink leaks, our Jonesboro emergency plumbing specialists fix all disposal issues 24/7.
          </p>
        </div>

        {/* Safety Warning Banner */}
        <div className="mb-12">
          <Alert className="border-border bg-card text-card-foreground shadow-sm">
            <ShieldAlert className="size-5 text-primary" />
            <AlertTitle className="font-semibold text-foreground text-base">
              Important Safety Notice
            </AlertTitle>
            <AlertDescription className="text-muted-foreground mt-1 text-sm sm:text-base">
              Disposals pack powerful high-torque motors. Always disconnect power at the main breaker before inspecting. If you are uncertain or notice leaks, call our on-call technicians immediately.
            </AlertDescription>
          </Alert>
        </div>

        {/* Problems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {problems.map((problem, idx) => <motion.div key={problem.id} data-index={idx} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-40px"
        }} transition={{
          duration: 0.35,
          delay: shouldReduceMotion ? 0 : idx * 0.08
        }} className="flex h-full">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="size-10 rounded-lg bg-accent/20 flex items-center justify-center text-foreground">
                      {getProblemIcon(problem.iconName)}
                    </div>
                    <Badge variant="outline" className="text-xs font-semibold">
                      {problem.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold font-default text-card-foreground">
                    {problem.title}
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="space-y-4 pt-1 flex-1 flex flex-col justify-between">
                  <div className="space-y-3">
                    <div>
                      <span className="text-xs uppercase font-bold tracking-wider text-muted-foreground block mb-1">
                        Symptom
                      </span>
                      <p className="text-sm text-card-foreground leading-relaxed">
                        {problem.symptoms}
                      </p>
                    </div>

                    <div>
                      <span className="text-xs uppercase font-bold tracking-wider text-muted-foreground block mb-1">
                        Frequent Root Cause
                      </span>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {problem.causes}
                      </p>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-border flex items-center justify-between text-xs">
                    <span className="text-muted-foreground font-medium">Urgency:</span>
                    <span className="font-semibold text-card-foreground">{problem.urgency}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}

          {/* Quick Troubleshooting / Prevention Card */}
          <motion.div data-index={problems.length} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-40px"
        }} transition={{
          duration: 0.35,
          delay: shouldReduceMotion ? 0 : problems.length * 0.08
        }} className="flex h-full md:col-span-2 lg:col-span-1">
            <Card className="flex flex-col justify-between w-full border border-primary/20 bg-primary/5 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="size-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
                    <Ban className="size-5" />
                  </div>
                  <Badge variant="default" className="text-xs font-semibold">
                    Safety Rules
                  </Badge>
                </div>
                <CardTitle className="text-xl font-bold font-default text-card-foreground">
                  Things To Avoid
                </CardTitle>
                <CardDescription className="text-sm">
                  Protect your plumbing system and avoid emergency disposal burnouts.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-3 flex-1 flex flex-col justify-between">
                <ul className="space-y-2.5">
                  {safetyTips.map((tip, tipIndex) => <li key={tipIndex} data-index={tipIndex} className="flex items-start gap-2 text-xs sm:text-sm text-card-foreground">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{tip}</span>
                    </li>)}
                </ul>

                <div className="pt-4 border-t border-border">
                  <Button asChild className="w-full font-semibold" variant="default" size="default">
                    <Link to="/problems/">
                      View All Plumbing Guides
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Bottom CTA Bar */}
        <div className="mt-12 md:mt-16 bg-muted/60 border border-border rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="max-w-xl">
            <h3 className="text-xl sm:text-2xl font-default font-bold text-foreground">
              Disposal Jammed or Leaking Right Now?
            </h3>
            <p className="mt-1 text-sm sm:text-base text-muted-foreground">
              Jonesboro Emergency Plumber provides prompt same-day repairs and brand replacements across Jonesboro, GA.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/contact/">
                <Wrench className="size-4 mr-2" />
                Book Fast Repair
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-semibold">
              <a href="tel:4708479076">
                <PhoneCall className="size-4 mr-2" />
                Call 24/7 Dispatch
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}